'use client';

import type { AxiosRequestConfig, AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import axios from 'axios';

import { CONFIG } from 'src/global-config';
import { DEV_LÄGE_AKTIVT, aktivtScenario, LÅNGSAM_FÖRDRÖJNING_MS } from 'src/lib/dev-lage';

/**
 * Vad ett tomt svar är för en given endpoint.
 *
 * En lista är det vanliga, så `[]` är standard. Endpoints som svarar med ett
 * objekt måste däremot få rätt form — ett `[]` där koden väntar sig
 * `{ kontakter: [...] }` är inte ett tomt tillstånd utan en krasch, och en
 * krasch bevisar ingenting om tomvyn.
 *
 * En `?? TOMT`-fallback i anropande kod räddar oss inte: `[]` är
 * sanningsvärde-sant och passerar rakt igenom den.
 *
 * Matchningen sker på exakt sökväg, inte delsträng, eftersom sökvägar är
 * prefix av varandra — `/api/kontakter/v1` svarar med ett objekt medan
 * `/api/kontakter/v1/sokbara` svarar med en lista.
 *
 * Layoutens endpoints ligger med från början: klockan, kontakterna och
 * att-göra-listan laddas på *varje* sida, så fel form där kraschar hela appen
 * oavsett vilken vy man ville titta på. Övriga fylls på domän för domän.
 */
const TOMMA_OBJEKTSVAR: Record<string, unknown> = {
  '/api/notifieringar/v1': {
    notifieringar: [],
    antalAlla: 0,
    antalOlästa: 0,
    antalArkiverade: 0,
  },
  '/api/kontakter/v1': { kontakter: [], antal: 0 },
};

/** Delsträngar som räcker för att veta att svaret är ett objekt. */
const TOMMA_OBJEKT_MÖNSTER = ['/installningar', '/hamtningsvy', '/statistik'];

function tomtSvar(url: string): unknown {
  const sökväg = url.split('?')[0].replace(/\/$/, '');

  if (sökväg in TOMMA_OBJEKTSVAR) {
    return TOMMA_OBJEKTSVAR[sökväg];
  }

  if (TOMMA_OBJEKT_MÖNSTER.some((del) => sökväg.includes(del))) {
    return {};
  }

  return [];
}

// ----------------------------------------------------------------------
const c = CONFIG;
const serverRequestUrl = CONFIG.serverUrl;
const axiosInstance = axios.create({
  baseURL: CONFIG.serverUrl,
});

/**
 * Utvecklingslägets scenariogrind.
 *
 * Ligger först av alla interceptorer så att den hinner byta ut anropet innan
 * något annat händer. Den byter `config.adapter`, alltså själva transporten —
 * anropet lämnar aldrig webbläsaren i något annat läge än `data`.
 *
 * Hela poängen: en polerad vy måste granskas i sina fyra tillstånd, och en
 * riktig databas kan inte beställas att vara tom eller trasig.
 */
axiosInstance.interceptors.request.use((config) => {
  if (!DEV_LÄGE_AKTIVT) {
    return config;
  }

  const scenario = aktivtScenario();

  if (scenario === 'laddar') {
    // Löftet infrias aldrig — vyn står kvar i sitt laddtillstånd.
    config.adapter = () => new Promise(() => {});
    return config;
  }

  if (scenario === 'fel') {
    config.adapter = () => {
      // Formen måste likna ett riktigt axios-fel, annars läser felhanterarna
      // runtom i appen `error.response.status` som undefined och tar fel gren.
      const fel = Object.assign(new Error('Framtvingat fel (dev-scenario)'), {
        config,
        response: {
          status: 500,
          statusText: 'Internal Server Error',
          data: {},
          headers: {},
          config,
        } as AxiosResponse,
      });
      return Promise.reject(fel);
    };
    return config;
  }

  if (scenario === 'tom') {
    config.adapter = () =>
      Promise.resolve<AxiosResponse>({
        data: tomtSvar(config.url ?? ''),
        status: 200,
        statusText: 'OK',
        headers: {},
        config,
      });
    return config;
  }

  if (scenario === 'langsam') {
    const ursprunglig = (config.adapter ?? axios.defaults.adapter) as AxiosAdapter;
    config.adapter = async (cfg: InternalAxiosRequestConfig) => {
      await new Promise((klar) => setTimeout(klar, LÅNGSAM_FÖRDRÖJNING_MS));
      return ursprunglig(cfg);
    };
    return config;
  }

  return config;
});

import { firebaseClientAuth } from '@/src/lib/firebase/clientApp';
import { visaFel } from 'src/lib/fel/visaFel';

axiosInstance.interceptors.request.use(async (config) => {
  let token = localStorage.getItem('accessToken');
  if (!token && firebaseClientAuth?.currentUser) {
    try {
      token = await firebaseClientAuth.currentUser.getIdToken();
      if (token) {
        localStorage.setItem('accessToken', token);
      }
    } catch {
      // ignore
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Set X-Organisation-Id header from app-context in localStorage,
  // but only if the caller has not already provided it explicitly.
  if (!config.headers['X-Organisation-Id']) {
    try {
      const appContextString = localStorage.getItem('app-context');
      if (appContextString) {
        const appContext = JSON.parse(appContextString);
        const orgId = appContext?.organisation?.id;
        if (orgId && orgId !== 'privatperson') {
          config.headers['X-Organisation-Id'] = orgId;
        }
      }
    } catch (error) {
      console.error('Kunde inte läsa organisation från localStorage', error);
    }
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const message =
      error?.response?.data?.message || error?.message || 'Ett oväntat fel skedde: ' + error;

    const originalRequest = error.config;

    // Om vi får 401 Unauthorized, token kan ha gått ut. Testa att hämta ny och gör om anropet.
    if (error?.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const currentUser = firebaseClientAuth?.currentUser;
        if (currentUser) {
          const newToken = await currentUser.getIdToken(true);
          localStorage.setItem('accessToken', newToken);
          axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          // Retry the original request with the new token
          return axiosInstance(originalRequest);
        }
      } catch (refreshError) {
        console.error('Failed to refresh Firebase token silently', refreshError);
      }
    }

    if (error?.response?.status === 429) {
      console.warn('Axios rate limit (429):', message);
    } else {
      console.error('Axios error:', message);
    }

    // Oväntade fel (5xx) och anrop som aldrig nådde fram får en central behandling: de
    // registreras med sin felreferens och användaren erbjuds att rapportera. Väntade fel
    // (validering, 404, konflikter) lämnas till anropsstället — där finns sammanhanget som
    // gör dem begripliga.
    const status = error?.response?.status;
    const ärServerfel = typeof status === 'number' && status >= 500;
    const nåddeAldrigFram = !error?.response && error?.code !== 'ERR_CANCELED';

    if (ärServerfel || nåddeAldrigFram) {
      try {
        visaFel(
          error,
          nåddeAldrigFram
            ? 'Kunde inte nå servern. Kontrollera din uppkoppling och försök igen.'
            : 'Något oväntat gick fel.'
        );
      } catch (visningsfel) {
        // Felvisningen får aldrig svälja det ursprungliga felet.
        console.error('Kunde inte visa felet för användaren', visningsfel);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async <T = unknown>(
  args: string | [string, AxiosRequestConfig]
): Promise<T> => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args, {}];

    const res = await axiosInstance.get<T>(url, config);

    return res.data;
  } catch (error) {
    console.error('Fetcher failed:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  // Sökvägarna är ASCII även om nycklarna är svenska — se projektets regel om URL-segment.
  fel: {
    klientfel: '/api/fel/v1/klientfel',
    rapport: (referens: string) => `/api/fel/v1/${referens}/rapport`,
    händelser: '/api/fel/v1/handelser',
    händelse: (referens: string) => `/api/fel/v1/handelser/${referens}`,
    status: (referens: string) => `/api/fel/v1/handelser/${referens}/status`,
  },
  kvot: {
    avslag: '/api/kvot/v1/avslag',
  },
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/api/auth/me',
    signIn: '/api/auth/sign-in',
    signUp: '/api/auth/sign-up',
  },
  demo: {
    // `profil` är demoprofilens ASCII-vägsegment, t.ex. 'bostadsrattsforening'.
    starta: (profil: string) => `/api/demo/${profil}`,
    återställ: (profil: string) => `/api/demo/${profil}/reset`,
    organisationer: '/api/demo/organisationer',
  },
  filuppladdning: (orgId: string) => `/api/filuppladdning/v1/${orgId}`,
  dokument: {
    mappar: (orgId: string) => `/api/dokument/v1/mappar/${orgId}`,
    mapp: (id: string) => `/api/dokument/v1/mappar/detalj/${id}`,
    skapaMapp: (orgId: string) => `/api/dokument/v1/mappar/${orgId}`,
    skapaUndermapp: (förälderId: string) => `/api/dokument/v1/mappar/${förälderId}/undermapp`,
    skapaStandard: (orgId: string) => `/api/dokument/v1/mappar/standard/${orgId}`,
    skapaStruktur: (orgId: string) => `/api/dokument/v1/mappar/struktur/${orgId}`,
    raderaMapp: (id: string) => `/api/dokument/v1/mappar/${id}`,
    laddaUppFil: (mappId: string) => `/api/dokument/v1/mappar/${mappId}/filer`,
    listaFiler: (mappId: string) => `/api/dokument/v1/mappar/${mappId}/filer`,
    flyttaMapp: (mappId: string) => `/api/dokument/v1/mappar/${mappId}/flytta`,
    driveRot: (orgId: string) => `/api/dokument/v1/drive-rot/${orgId}`,
    sharepoint: {
      authUrl: (orgId: string) => `/api/dokument/v1/sharepoint/auth-url?orgId=${orgId}`,
      callback: () => `/api/dokument/v1/sharepoint/callback`,
      status: (orgId: string) => `/api/dokument/v1/sharepoint/status/${orgId}`,
      disconnect: (orgId: string) => `/api/dokument/v1/sharepoint/disconnect/${orgId}`,
      excel: {
        worksheets: (orgId: string, itemId: string, driveId?: string) =>
          `/api/dokument/v1/sharepoint/excel/worksheets?orgId=${orgId}&itemId=${encodeURIComponent(itemId)}${driveId ? `&driveId=${encodeURIComponent(driveId)}` : ''}`,
        range: (orgId: string, itemId: string, sheetName: string, range: string, driveId?: string) =>
          `/api/dokument/v1/sharepoint/excel/range?orgId=${orgId}&itemId=${encodeURIComponent(itemId)}&sheetName=${encodeURIComponent(sheetName)}&range=${encodeURIComponent(range)}${driveId ? `&driveId=${encodeURIComponent(driveId)}` : ''}`,
        updateRange: () => `/api/dokument/v1/sharepoint/excel/range`,
        addRow: () => `/api/dokument/v1/sharepoint/excel/table/row`,
        parseUrl: () => `/api/dokument/v1/sharepoint/excel/parse-url`,
        editLink: (orgId: string, itemId: string, driveId?: string) =>
          `/api/dokument/v1/sharepoint/excel/edit-link?orgId=${orgId}&itemId=${encodeURIComponent(itemId)}${driveId ? `&driveId=${encodeURIComponent(driveId)}` : ''}`,
      },
    },
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  organisation: {
    root: '/api/organisation/v1',
    anvandare: '/api/organisation/v1/anvandare',
    visa: '/api/organisation/v1/visa',
    sök: '/api/organisation/v1/sok',
    verifiera: '/api/v1/organisation/verifiera',
    uppdatera: (orgId: string) => `/api/organisation/v1/${orgId}`,
    roller: (orgId: string) => `/api/organisation/v1/${orgId}/roller`,
    menyinställningar: (orgId: string) => `/api/organisation/v1/${orgId}/menyinstallningar`,
    // Användarna i organisationen och deras roller där — inte att förväxla med
    // `anvandare` ovan, som listar den inloggades organisationer.
    användare: {
      lista: (orgId: string) => `/api/organisation/v1/${orgId}/anvandare`,
      tilldelbaraRoller: (orgId: string) => `/api/organisation/v1/${orgId}/anvandare/roller`,
      roller: (orgId: string, användarId: string) =>
        `/api/organisation/v1/${orgId}/anvandare/${användarId}/roller`,
    },
    stadgar: {
      översikt: (orgId: string) => `/api/stadgar/v1/${orgId}`,
      gällande: (orgId: string) => `/api/stadgar/v1/${orgId}/gallande`,
      original: (orgId: string, stadgarId: string) =>
        `/api/stadgar/v1/${orgId}/versioner/${stadgarId}/original`,
      förslag: (orgId: string) => `/api/stadgar/v1/${orgId}/forslag`,
      förslagParagrafer: (orgId: string) => `/api/stadgar/v1/${orgId}/forslag/paragrafer`,
      förslagParagraf: (orgId: string, paragrafId: string) =>
        `/api/stadgar/v1/${orgId}/forslag/paragrafer/${paragrafId}`,
      förslagOrdning: (orgId: string) => `/api/stadgar/v1/${orgId}/forslag/paragraf-ordning`,
      stämmobeslut: (orgId: string) => `/api/stadgar/v1/${orgId}/forslag/stammobeslut`,
      registreraHosBolagsverket: (orgId: string) =>
        `/api/stadgar/v1/${orgId}/forslag/registrera-hos-bolagsverket`,
      adminGällande: (orgId: string) => `/api/stadgar/v1/${orgId}/admin/gallande`,
      adminRaderaVersion: (orgId: string, stadgarId: string) =>
        `/api/stadgar/v1/${orgId}/admin/versioner/${stadgarId}`,
      adminRaderaAlla: (orgId: string) => `/api/stadgar/v1/${orgId}/admin/alla`,
    },
    rakenskapsar: {
      root: '/api/organisation/v1/rakenskapsar',
      senaste: '/api/organisation/v1/rakenskapsar/senaste',
    },
    ansökan: {
      root: '/api/organisation/v1/ansokan',
      mina: '/api/organisation/v1/ansokan/mina',
      förOrganisation: (orgId: string) => `/api/organisation/v1/ansokan/organisation/${orgId}`,
      status: '/api/organisation/v1/ansokan/status',
    },
  },
  inbjudan: {
    organisation: (orgId: string) => `/api/inbjudan/v1/organisation/${orgId}`,
    epost: (orgId: string) => `/api/inbjudan/v1/organisation/${orgId}/epost`,
    info: (id: string) => `/api/inbjudan/v1/${id}/info`,
    acceptera: (id: string) => `/api/inbjudan/v1/${id}/acceptera`,
  },
  bank: {
    banktransaktioner: {
      root: '/api/bank/v1/banktransaktioner',
      hämta: (orgId: string) => `/api/bank/v1/banktransaktioner/${orgId}`,
      hämtaNya: (orgId?: string) => `/api/bank/v1/banktransaktioner/hamta-nya/${orgId}`,
      filuppladdning: (orgId: string) => `/api/bank/v1/banktransaktioner/filuppladdning/${orgId}`,
      skapaInköp: (orgId: string) => {
        const endpoint = `/api/bank/v1/banktransaktioner/inkop/${orgId}`;
        return endpoint;
      },
      attBokföra: (orgId: string) => `/api/bank/v1/banktransaktioner-att-bokfora/${orgId}`,
      förBokföringskontonummer: (orgId: string, bokföringskontonummer: number) => `/api/bank/v1/banktransaktioner/${orgId}/bokforingskonto/${bokföringskontonummer}`,
      flyttaBankkonto: (orgId: string, banktransaktionId: string) => `/api/bank/v1/banktransaktioner/${orgId}/${banktransaktionId}/flytta-bankkonto`,
      markeraBokförd: (orgId: string, banktransaktionId: string) => `/api/bank/v1/banktransaktioner/${orgId}/${banktransaktionId}/markera-bokford`,
      kandidaterForAffärshändelse: (orgId: string, affärshändelseId: string) => `/api/bank/v1/banktransaktioner/${orgId}/kandidater-for-affarshandelse/${affärshändelseId}`,
      sökForAffärshändelse: (orgId: string, affärshändelseId: string) => `/api/bank/v1/banktransaktioner/${orgId}/soek-for-affarshandelse/${affärshändelseId}`,
      taBortKoppling: (orgId: string, affärshändelseId: string) => `/api/bank/v1/banktransaktioner/${orgId}/koppla-till-affarshandelse/${affärshändelseId}`,
      kopplaTillAffärshändelse: (orgId: string) => `/api/bank/v1/banktransaktioner/${orgId}/koppla-till-affarshandelse`,
    },
    koppla: {
      banker: '/api/bank/v1/koppla/banker',
      connect: (orgId: string) => `/api/bank/v1/koppla/connect?orgId=${orgId}`,
      complete: (orgId: string) => `/api/bank/v1/koppla/complete/${orgId}`,
    },
    konton: {
      hämta: (orgId: string) => `/api/bank/v1/konton/${orgId}`,
      skapa: (orgId: string) => `/api/bank/v1/konton/${orgId}`,
      namn: (id: string) => `/api/bank/v1/konton/${id}/namn`,
      saldoHistorik: (id: string, manader: number) => `/api/bank/v1/konton/${id}/saldo-historik?manader=${manader}`,
      saldon: (orgId: string) => `/api/bank/v1/konton/saldon/${orgId}`,
      bokföringskontonummer: (id: string) =>
        `/api/bank/v1/konton/${id}/bokforingskontonummer`,
      kontoutdrag: (id: string) => `/api/bank/v1/konton/${id}/kontoutdrag`,
      dubbletter: (orgId: string) => `/api/bank/v1/konton/${orgId}/dubbletter`,
      dubblettkandidater: (orgId: string) => `/api/bank/v1/konton/${orgId}/dubblettkandidater`,
      ignoreraDubblettkandidat: (orgId: string) =>
        `/api/bank/v1/konton/${orgId}/dubblettkandidater/ignorera`,
      radera: (id: string) => `/api/bank/v1/konton/${id}`,
      slåIhop: (id: string) => `/api/bank/v1/konton/${id}/sla-ihop`,
      lösOrphaner: (orgId: string) => `/api/bank/v1/konton/${orgId}/los-orphaner`,
    },
    interna: {
      hämta: (orgId: string) => `/api/bank/v1/interna-transaktioner/${orgId}`,
      bokför: (orgId: string) => `/api/bank/v1/interna-transaktioner/bokfor/${orgId}`,
      reset: (orgId: string) => `/api/bank/v1/interna-transaktioner/reset/${orgId}`,
    },
    bokföringSparkonton: {
      hämta: (orgId: string) => `/api/bank/v1/bokforing-sparkonton/${orgId}`,
      bokför: (orgId: string) => `/api/bank/v1/bokforing-sparkonton/bokfor/${orgId}`,
    },
  },
  lön: {
    root: '/api/lon/v1',
    filuppladdning: '/api/lon/v1/filuppladdning',
    arbetsgivardeklarationer: {
      root: '/api/lon/v1/arbetsgivardeklarationer',
      filuppladdning: '/api/lon/v1/arbetsgivardeklarationer/filuppladdning',
    },
  },
  inköp: {
    root: '/api/inkop/v2',
    hämta: (orgId: string) => `/api/inkop/v2/${orgId}`, // Deprecated
    omatchadeInköp: (orgId: string) => `/api/inkop/v2/${orgId}/omatchade-inkop`,
    underlagCount: (orgId: string) => `/api/inkop/v2/${orgId}/underlag-count`,
    matchningar: (orgId: string) => `/api/inkop/v2/${orgId}/matchningar`,
    beräknaMatchningar: (orgId: string) => `/api/inkop/v2/${orgId}/berakna-matchningar`,
    matchningForTransaktion: (orgId: string, banktransaktionId: string) => `/api/inkop/v2/${orgId}/matchning-for-transaktion/${banktransaktionId}`,
    omatchadeUnderlag: (orgId: string) => `/api/inkop/v2/${orgId}/omatchade-underlag`,
    allaUnderlag: (orgId: string) => `/api/inkop/v2/${orgId}/underlag`,
    uppladdaUnderlag: (orgId: string) => `/api/inkop/v2/inkopsunderlag/${orgId}`,
    uppladdaUnderlagStatus: '/api/inkop/v2/inkopsunderlag/status',
    draft: (orgId: string) => `/api/inkop/v2/${orgId}/draft`,
    spara: (orgId: string) => `/api/inkop/v2/${orgId}/spara`,
    dubblettAffärshändelse: (orgId: string, underlagId: string) => `/api/inkop/v2/${orgId}/dubblett-affarshandelse/${underlagId}`,
  },
  momsdeklaration: {
    root: '/api/momsdeklaration',
    hämta: (orgId: string) => `/api/momsdeklaration/${orgId}`,
  },
  kredit: {
    root: '/api/kredit/v1',
    filuppladdning: (orgId: string) => `/api/kredit/v1/filuppladdning/parallellt/${orgId}`,
    status: '/api/kredit/v1/status/parallellt',
    hämta: (orgId: string) => `/api/kredit/v1/${orgId}`,
  },
  kundfaktura: {
    root: '/api/kundfaktura/v1',
    hämta: (orgId: string) => `/api/kundfaktura/v1/${orgId}`,
    filuppladdning: (orgId: string) => `/api/kundfaktura/v1/filuppladdning/parallellt/${orgId}`,
    automatchaBetalningar: (orgId: string) => `/api/kundfaktura/v1/automatcha-betalningar/${orgId}`,
    status: '/api/kundfaktura/v1/status/parallellt',
    radera: (orgId: string) => `/api/kundfaktura/v1/${orgId}`,
    markeraBokförd: (orgId: string, underlagId: string) => `/api/kundfaktura/v1/${orgId}/markera-bokförd/${underlagId}`,
  },
  skatt: {
    arbetsgivardeklarationer: {
      root: '/api/skatt/v1/arbetsgivardeklarationer',
      filuppladdning: '/api/skatt/v1/arbetsgivardeklarationer/filuppladdning',
    },
    skatteutdrag: {
      root: '/api/skatt/v1/skatteutdrag',
      filuppladdning: '/api/skatt/v1/skatteutdrag/filuppladdning',
      repareraLänkning: (orgId: string) => `/api/skatt/v1/skatteutdrag/${orgId}/reparera-banktransaktionslänkning`,
    },
    transaktioner: {
      root: '/api/skatt/v1/transaktioner',
      visa: (orgId: string, id: string) => `/api/skatt/v1/transaktioner/${orgId}/${id}`,
    },
    inkomstdeklarationer: {
      hämta: (orgId: string) => `/api/skatt/v1/inkomstdeklarationer/${orgId}`,
    },
  },
  bokföring: {
    verifikationstyper: '/api/bokforing/bokforingsposter/v1/verifikationstyper',
    affärshändelser: {
      root: '/api/bokforing/affarshandelser/v1',
      typer: '/api/bokforing/affarshandelser/v1/typer',
      hämta: (orgId: string, id: string) =>
        `/api/bokforing/affarshandelser/v1/hamta/${orgId}/${id}`,
      läggTillVerifikation: (orgId: string, id: string) =>
        `/api/bokforing/affarshandelser/v1/${orgId}/${id}/lagg-till-verifikation`,
      hämtaAllaFörOrg: (orgId: string) =>
        `/api/bokforing/affarshandelser/v1/hamta/${orgId}`,
      sparaManuell: (orgId: string) =>
        `/api/bokforing/affarshandelser/v1/spara/manuell/${orgId}`,
      skapaFörslagMedAi: (orgId: string) =>
        `/api/bokforing/affarshandelser/v1/ai-forslag/${orgId}`,
      spara: (orgId: string) => `/api/bokforing/affarshandelser/v1/spara/${orgId}`,
      sparaBatch: (orgId: string) => `/api/bokforing/affarshandelser/v1/spara/batch/${orgId}`,
      duplikatkontroll: (orgId: string) =>
        `/api/bokforing/affarshandelser/v1/duplikatkontroll/${orgId}`,

      draft: (orgId: string) => `/api/bokforing/affarshandelser/v1/draft/${orgId}`,
      radera: (orgId: string, id: string) =>
        `/api/bokforing/affarshandelser/v1/radera/${orgId}/${id}`,
      raderaBatch: (orgId: string) =>
        `/api/bokforing/affarshandelser/v1/radera/batch/${orgId}`,
      kategorier: '/api/bokforing/affarshandelser/v1/kategorier',
      saldoHistorik: (orgId: string, kontoNummer: number, manader: number) =>
        `/api/bokforing/affarshandelser/v1/saldo-historik/${orgId}?kontoNummer=${kontoNummer}&manader=${manader}`,
      resultatHistorik: (orgId: string, manader: number) =>
        `/api/bokforing/affarshandelser/v1/resultat-historik/${orgId}?manader=${manader}`,
      perKonto: (orgId: string) => `/api/bokforing/affarshandelser/v1/by-konto/${orgId}`,
    },
    kontoplan: (orgId: string) => `/api/bokforing/v1/kontoplan/${orgId}`,
    resultaträkning: {
      hämta: (orgId: string) => `/api/bokforing/resultatrakning/v1/${orgId}`,
      månadsvis: (orgId: string) => `/api/bokforing/resultatrakning/v1/${orgId}/manadsvis`,
    },
    lön: {
      utkast: '/api/bokforing/v1/lon/utkast',
      bokfor: '/api/bokforing/v1/lon/bokfor',
    },
    bokfor: {
      root: '/api/bokforing/bokfor/v1',
      kundfaktura: '/api/bokforing/bokfor/v1/kundfaktura',
      arbetsgivardeklaration: '/api/bokforing/bokfor/v1/arbetsgivardeklaration',
      verifikation: {
        vänd: '/api/bokforing/bokfor/v1/verifikation/vand',
        slutför: '/api/bokforing/bokfor/v1/verifikation/slutfor',
      },
      skatt: {
        skattetransaktion: '/api/skatt/v1/skattetransaktion/bokfor',
        batch: '/api/skatt/v1/skattetransaktion/bokfor/batch',
      },
      loner: {
        batch: '/api/lon/v1/bokfor/batch',
      },
    },
    bokslut: {
      root: '/api/bokforing/v1/bokslut',
      hämta: (orgId: string) => `/api/bokforing/bokslut/v1/ett/${orgId}`,
      hämtaAlla: (orgId: string) => `/api/bokforing/bokslut/v1/${orgId}`,
    },
    bokslutV2: {
      hämtaAlla: (orgId: string) => `/api/bokforing/bokslut/v2/${orgId}`,
      hämta: (orgId: string, start: string, slut: string) =>
        `/api/bokforing/bokslut/v2/${orgId}/${start}/${slut}`,
      berakna: (orgId: string, start: string, slut: string) =>
        `/api/bokforing/bokslut/v2/${orgId}/${start}/${slut}/berakna`,
      pdf: (orgId: string, start: string, slut: string) =>
        `/api/bokforing/bokslut/v2/${orgId}/${start}/${slut}/pdf`,
      aktiebok: (orgId: string) =>
        `/api/bokforing/bokslut/v2/${orgId}/aktiebok`,
      arsredovisningUnderlag: (orgId: string, start: string, slut: string) =>
        `/api/bokforing/bokslut/v2/${orgId}/${start}/${slut}/arsredovisning-underlag`,
      arsredovisningPdf: (orgId: string, start: string, slut: string) =>
        `/api/bokforing/bokslut/v2/${orgId}/${start}/${slut}/arsredovisning/pdf`,
      vinstdisposition: (orgId: string, start: string, slut: string) =>
        `/api/bokforing/bokslut/v2/${orgId}/${start}/${slut}/vinstdisposition`,
      dataimportÅrsredovisning: (orgId: string, start: string, slut: string) =>
        `/api/bokforing/bokslut/v2/${orgId}/${start}/${slut}/dataimport-arsredovisning`,
      sparaDataimportÅrsredovisning: (orgId: string, start: string, slut: string) =>
        `/api/bokforing/bokslut/v2/${orgId}/${start}/${slut}/dataimport-arsredovisning/spara`,
    },
    // Bolagsverkets register över digitalt inlämnade årsredovisningar. Fristående från vår egen
    // årsredovisningsvy nedan: det här är andras uppgifter, hämtade på organisationsnummer.
    årsredovisningsarkiv: {
      sök: (orgnummer: string) => `/api/arsredovisningsarkiv/v1/${orgnummer}`,
      dokument: (orgnummer: string, dokumentId: string) =>
        `/api/arsredovisningsarkiv/v1/${orgnummer}/dokument/${dokumentId}`,
    },
    // Visnings-JSON för årsredovisningsvyn. Nycklarna behåller å/ä/ö, URL:erna är ren ASCII.
    årsredovisning: {
      senaste: (orgId: string) => `/api/v1/organisation/${orgId}/arsredovisning/senaste`,
      publik: (slug: string) => `/api/public/v1/arsredovisning/${slug}`,
      publikPdf: (slug: string) => `/api/public/v1/arsredovisning/${slug}/pdf`,
    },
    bokslutProcess: {
      hämtaEllerSkapa: (orgId: string, start: string, slut: string) =>
        `/api/bokslut-process/v1/${orgId}/${start}/${slut}`,
      uppdateraSteg: (processId: string) => `/api/bokslut-process/v1/${processId}/steg`,
      slutför: (processId: string) => `/api/bokslut-process/v1/${processId}/slutfor`,
      periodisering: (orgId: string, start: string, slut: string) =>
        `/api/bokslut-process/v1/${orgId}/periodisering?rakenskapsarStart=${start}&rakenskapsarSlut=${slut}`,
    },
    details: '/api/arende/v1/',
    search: '/api/arende/v1/search',
    fil: {
      root: '/api/bokforing/v1/filuppladdning',
      affärshändelseunderlag: (orgId: string) =>
        `/api/bokforing/v1/filuppladdning/${orgId}`,
      kundfaktura: '/api/bokforing/v1/filuppladdning/kundfaktura',
      arbetsgivardeklaration: '/api/bokforing/v1/filuppladdning/arbetsgivardeklaration',
    },
    importera: {
      sie4Forhandsgranska: (orgId: string) =>
        `/api/bokforing/importera/v1/sie4/${orgId}/forhandsgranska`,
      sie4Importera: (orgId: string) =>
        `/api/bokforing/importera/v1/sie4/${orgId}/importera`,
      huvudbokTolka: (orgId: string) =>
        `/api/bokforing/importera/v1/huvudbok/${orgId}/tolka`,
      huvudbokSparaBatch: (orgId: string) =>
        `/api/bokforing/importera/v1/huvudbok/${orgId}/spara-batch`,
      annanKlassificera: (orgId: string) =>
        `/api/bokforing/importera/v1/annan/${orgId}/klassificera`,
      balansräkningTolka: (orgId: string) =>
        `/api/bokforing/importera/v1/balansrakning/${orgId}/tolka`,
      balansräkningSpar: (orgId: string) =>
        `/api/bokforing/importera/v1/balansrakning/${orgId}/spara`,
      balansräkningKorrigeraMomsResidual: (orgId: string) =>
        `/api/bokforing/importera/v1/balansrakning/${orgId}/korrigera-moms-residual`,
      saldonHärled: (orgId: string) =>
        `/api/bokforing/importera/v1/saldon/${orgId}/harled`,
      saldonSpara: (orgId: string) =>
        `/api/bokforing/importera/v1/saldon/${orgId}/spara`,
    },
    exportera: {
      sie4: (orgId: string) => `/api/bokforing/exportera/v1/sie4/${orgId}`,
      grundbokJson: (orgId: string) => `/api/bokforing/grundbok/v1/${orgId}`,
      huvudbokPdf: (orgId: string) => `/api/bokforing/huvudbok/v1/${orgId}/pdf`,
      grundbokPdf: (orgId: string) => `/api/bokforing/grundbok/v1/${orgId}/pdf`,
    },
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  legitimering: {
    starta: '/api/legitimering/v1/starta',
    slutfor: '/api/legitimering/v1/slutfor',
    status: '/api/legitimering/v1/status',
  },
  användare: {
    root: '/api/anvandare/v1',
    laddaUppAvatar: '/api/anvandare/v1/avatar',
    onboardingKlar: '/api/anvandare/v1/onboarding-klar',
    leaveOrganisation: (orgId: string) => `/api/anvandare/v1/organisation/${orgId}`,
    admin: {
      alla: '/api/anvandare/v1/organisation-admin/anvandare',
      uppdateraOrganisationer: (id: string) => `/api/anvandare/v1/organisation-admin/anvandare/${id}/organisationer`,
    },
  },
  ärende: {
    list: '/api/arende/v1',
    details: '/api/arende/v1/',
    search: '/api/arende/v1/search',
  },
  styrelse: {
    möte: {
      root: '/api/styrelse/mote/v1',
      nästa: (orgId: string) => `/api/styrelse/mote/v1/nasta/${orgId}`,
      hämta: (moteId: string) => `/api/styrelse/mote/v1/${moteId}`,
      uppdatera: (moteId: string) => `/api/styrelse/mote/v1/${moteId}`,
      läggTillÄmne: (moteId: string) => `/api/styrelse/mote/v1/${moteId}/amne`,
      uppdateraProtokoll: (moteId: string) => `/api/styrelse/mote/v1/${moteId}/protokoll`,
      läggTillProtokollPunkt: (moteId: string) => `/api/styrelse/mote/v1/${moteId}/protokoll/punkt`,
      protokollPdf: (moteId: string) => `/api/styrelse/mote/v1/${moteId}/protokoll/pdf`,
      starta: (moteId: string) => `/api/styrelse/mote/v1/${moteId}/starta`,
      avsluta: (moteId: string) => `/api/styrelse/mote/v1/${moteId}/avsluta`,
      arkiv: (orgId: string) => `/api/styrelse/mote/v1/arkiv/${orgId}`,
      googleDocs: (moteId: string) => `/api/styrelse/mote/v1/${moteId}/protokoll/google-docs`,
      delaUtkast: (moteId: string) => `/api/styrelse/mote/v1/${moteId}/protokoll/dela-utkast`,
      importeraTranskription: (moteId: string) => `/api/styrelse/mote/v1/${moteId}/protokoll/importera-transkription`,
      sparaTranskription: (moteId: string) => `/api/styrelse/mote/v1/${moteId}/transkription`,
    },
    signering: {
      dela: (moteId: string) => `/api/styrelse/protokoll-signering/v1/${moteId}/dela`,
      starta: (moteId: string) => `/api/styrelse/protokoll-signering/v1/${moteId}/starta`,
      slutfor: (moteId: string) => `/api/styrelse/protokoll-signering/v1/${moteId}/slutfor`,
      status: (moteId: string) => `/api/styrelse/protokoll-signering/v1/${moteId}/status`,
    },
    ledamot: {
      lista: (orgId: string) => `/api/styrelse/ledamot/v1/${orgId}`,
      skapa: (orgId: string) => `/api/styrelse/ledamot/v1/${orgId}`,
      uppdatera: (ledamotId: string) => `/api/styrelse/ledamot/v1/${ledamotId}`,
      radera: (ledamotId: string) => `/api/styrelse/ledamot/v1/${ledamotId}`,
    },
  },
  försäkringar: {
    root: (orgId: string) => `/api/organisation/${orgId}/forsakringar`,
    komplettera: (orgId: string, forsakringId: string) => `/api/organisation/${orgId}/forsakringar/${forsakringId}/komplettera`,
  },
  karta: {
    root: '/api/kart/v1',
    hämta: (brfId: string) => `/api/kart/v1/${brfId}`,
    poi: (brfId: string) => `/api/kart/v1/${brfId}/poi`,
    taBortPoi: (id: string) => `/api/kart/v1/poi/${id}`,
    favoriter: '/api/kart/v1/favoriter',
    hemprofil: (poiId: string) => `/api/kart/v1/poi/${poiId}/hemprofil`,
    kommentarer: (poiId: string) => `/api/kart/v1/poi/${poiId}/kommentarer`,
  },
  fastighet: {
    minaHem: '/api/fastighet/v1/mina-hem',
    // Spotpriset för ett valt elområde. Egen endpoint så att elkortets rullgardin kan hämta om
    // priset utan att dra hela hem-dashboarden (sophämtning, paket, brevlåda) en gång till.
    spotpris: '/api/fastighet/v1/mina-hem/spotpris',
    // Närmaste hållplatser och avgångar (Trafiklab). Sökvägen är ASCII, nycklarna svenska.
    kommunikationer: '/api/fastighet/v1/mina-hem/kommunikationer',
    kommunikationerBeräkna: '/api/fastighet/v1/mina-hem/kommunikationer/berakna',
    // Bygglovshandlingar från Stockholms stads Bygg- och plantjänsten. Användarscopad som
    // planlösningen — historiken hör till hemmet, inte till föreningen. Sökvägen är ASCII,
    // nycklarna behåller sina svenska bokstäver.
    dokumentation: {
      översikt: '/api/bygglov/v1',
      hämta: '/api/bygglov/v1/hamta',
      fastighetsbeteckning: '/api/bygglov/v1/fastighetsbeteckning',
      laddaUppFil: (dokumentId: string) => `/api/bygglov/v1/dokument/${dokumentId}/fil`,
    },
    hemprofilInställningar: (poiId: string) => `/api/fastighet/v1/mina-hem/profil/${poiId}`,
    interiors: (fastighetsId: string) => `/api/fastighet/v1/buildings/${fastighetsId}/interiors`,
    latestInterior: (fastighetsId: string) => `/api/fastighet/v1/buildings/${fastighetsId}/interior/latest`,
    interiorStatus: (ritningId: string) => `/api/fastighet/v1/interior/${ritningId}/status`,
    interiorObjects: (ritningId: string) => `/api/fastighet/v1/interior/${ritningId}/objects`,
    addInteriorObject: (ritningId: string) => `/api/fastighet/v1/interior/${ritningId}/objects`,
    deleteInteriorObject: (objektId: string) => `/api/fastighet/v1/interior-objects/${objektId}`,
    updateInteriorObject: (objektId: string) => `/api/fastighet/v1/interior-objects/${objektId}`,
    byggnader: {
      lista: '/api/fastighet/v1/byggnadsmodell',
      skapa: '/api/fastighet/v1/byggnadsmodell',
      detalj: (id: string) => `/api/fastighet/v1/byggnadsmodell/${id}`,
      status: (id: string) => `/api/fastighet/v1/byggnadsmodell/${id}/status`,
      taBort: (id: string) => `/api/fastighet/v1/byggnadsmodell/${id}`,
    },
    // Planlösningen är personlig och ligger därför under /api/hem, inte under organisationen.
    // Sökvägen är ASCII (planlosning) medan nyckeln behåller svenska bokstäver.
    planlösning: {
      root: '/api/hem/v1/planlosning',
      detalj: (id: string) => `/api/hem/v1/planlosning/${id}`,
      status: (id: string) => `/api/hem/v1/planlosning/${id}/status`,
      aiKommando: (id: string) => `/api/hem/v1/planlosning/${id}/ai-kommando`,
      versioner: (id: string) => `/api/hem/v1/planlosning/${id}/versioner`,
      återställ: (id: string, versionsnummer: number) =>
        `/api/hem/v1/planlosning/${id}/versioner/${versionsnummer}/aterstall`,
    },
    // Hus 3D — den AI-genererade 3D-modellen av användarens eget hem. Personlig som planlösningen,
    // därför under /api/hem. En modell per hem, så inga id:n i sökvägen för själva modellen.
    hus3d: {
      root: '/api/hem/v1/hus-3d',
      status: '/api/hem/v1/hus-3d/status',
      källor: '/api/hem/v1/hus-3d/kallor',
      generera: '/api/hem/v1/hus-3d/generera',
      genereraMedFiler: '/api/hem/v1/hus-3d/generera-med-filer',
      förbättra: '/api/hem/v1/hus-3d/forbattra',
      ångra: '/api/hem/v1/hus-3d/angra',
      foton: '/api/hem/v1/hus-3d/foton',
      foto: (id: string) => `/api/hem/v1/hus-3d/foton/${id}`,
      förhandsgranskaKomponent: '/api/hem/v1/hus-3d/komponenter/forhandsgranska',
      komponenter: '/api/hem/v1/hus-3d/komponenter',
      komponent: (id: string) => `/api/hem/v1/hus-3d/komponenter/${id}`,
      komponentdokument: (id: string) => `/api/hem/v1/hus-3d/komponenter/${id}/dokument`,
      komponentdokumentMedId: (id: string, dokumentId: string) =>
        `/api/hem/v1/hus-3d/komponenter/${id}/dokument/${dokumentId}`,
      komponenthistorik: (id: string) => `/api/hem/v1/hus-3d/komponenter/${id}/historik`,
      komponentlive: (id: string) => `/api/hem/v1/hus-3d/komponenter/${id}/live`,
    },
    // Bygglovsguiden — ansökningar för en åtgärd på det egna hemmet. Personlig, därför /api/hem.
    bygglovsansökan: {
      root: '/api/hem/v1/bygglovsansokan',
      typer: '/api/hem/v1/bygglovsansokan/typer',
      hus: '/api/hem/v1/bygglovsansokan/hus',
      detalj: (id: string) => `/api/hem/v1/bygglovsansokan/${id}`,
      status: (id: string) => `/api/hem/v1/bygglovsansokan/${id}/status`,
      pröva: (id: string) => `/api/hem/v1/bygglovsansokan/${id}/prova`,
      hämtaPlanlösning: (id: string) => `/api/hem/v1/bygglovsansokan/${id}/hamta-planlosning`,
      aiBeskrivning: (id: string) => `/api/hem/v1/bygglovsansokan/${id}/ai/beskrivning`,
      bilagor: (id: string) => `/api/hem/v1/bygglovsansokan/${id}/bilagor`,
      arkivritning: (id: string) => `/api/hem/v1/bygglovsansokan/${id}/bilagor/arkiv`,
      bilaga: (id: string, bilagaId: string) => `/api/hem/v1/bygglovsansokan/${id}/bilagor/${bilagaId}`,
      ritning: (id: string, handlingstyp: string) =>
        `/api/hem/v1/bygglovsansokan/${id}/ritningar/${handlingstyp}`,
      paket: (id: string) => `/api/hem/v1/bygglovsansokan/${id}/paket`,
    },
  },
  kanaler: {
    // Organisationen kommer ur X-Organisation-Id och avsändaren ur inloggningen, som
    // request-interceptorn ovan sätter. Därför tar ingen av dem några id:n.
    lista: '/api/kanaler/v1',
    skapa: '/api/kanaler/v1',
    uppdatera: (id: string) => `/api/kanaler/v1/${id}`,
    arkivering: (id: string) => `/api/kanaler/v1/${id}/arkivering`,
    inlägg: (kanalId: string) => `/api/kanaler/v1/${kanalId}/inlagg`,
    raderaInlägg: (inläggId: string) => `/api/kanaler/v1/inlagg/${inläggId}`,
    reaktion: (inläggId: string) => `/api/kanaler/v1/inlagg/${inläggId}/reaktion`,
    bilder: '/api/kanaler/v1/bilder',
  },
  kontakter: {
    // Ägaren är alltid den inloggade och organisationen kommer ur X-Organisation-Id,
    // som request-interceptorn ovan sätter. Därför tar ingen av dem några id:n.
    lista: '/api/kontakter/v1',
    sökbara: '/api/kontakter/v1/sokbara',
    läggTill: '/api/kontakter/v1',
    dölj: (id: string) => `/api/kontakter/v1/${id}`,
    fäst: (id: string) => `/api/kontakter/v1/${id}/fast`,
  },
  tvättstuga: {
    // Organisationen kommer ur X-Organisation-Id, som request-interceptorn ovan sätter —
    // ingen endpoint tar något orgId.
    lista: '/api/tvattstuga/v1',
    skapa: '/api/tvattstuga/v1',
    detalj: (id: string) => `/api/tvattstuga/v1/${id}`,
    schema: (id: string) => `/api/tvattstuga/v1/${id}/schema`,
    boka: '/api/tvattstuga/v1/bokningar',
    minaBokningar: '/api/tvattstuga/v1/mina-bokningar',
    avboka: (bokningId: string) => `/api/tvattstuga/v1/bokningar/${bokningId}`,
  },
  fordonspool: {
    // Bilpool, lådcykelpool och släp är samma API — typen skiljer dem åt och går alltid över
    // tråden som ASCII-kod ('bil' | 'ladcykel' | 'slap'), aldrig som enumnamn med å/ä.
    //
    // Organisationen kommer ur X-Organisation-Id, som request-interceptorn ovan sätter.
    översikt: '/api/fordonspool/v1/oversikt',
    fordon: '/api/fordonspool/v1/fordon',
    fordonDetalj: (id: string) => `/api/fordonspool/v1/fordon/${id}`,
    boka: '/api/fordonspool/v1/bokningar',
    minaBokningar: '/api/fordonspool/v1/mina-bokningar',
    avboka: (bokningId: string) => `/api/fordonspool/v1/bokningar/${bokningId}`,
    inställningar: '/api/fordonspool/v1/installningar',
  },
  samåkning: {
    root: '/api/samakning/v1',
    minaResor: '/api/samakning/v1/mina-resor',
    detalj: (id: string) => `/api/samakning/v1/${id}`,
    anmäl: (id: string) => `/api/samakning/v1/${id}/anmal`,
    avanmäl: (id: string) => `/api/samakning/v1/${id}/avanmal`,
    ställIn: (id: string) => `/api/samakning/v1/${id}/installd`,
    taxialternativ: (id: string) => `/api/samakning/v1/${id}/taxi-alternativ`,
    godkänn: (anmälanId: string) => `/api/samakning/v1/anmalningar/${anmälanId}/godkann`,
    avböj: (anmälanId: string) => `/api/samakning/v1/anmalningar/${anmälanId}/avboj`,
    // Stående rutter: regeln som föder resorna.
    rutter: '/api/samakning/v1/rutter',
    rutt: (ruttId: string) => `/api/samakning/v1/rutter/${ruttId}`,
    avaktiveraRutt: (ruttId: string) => `/api/samakning/v1/rutter/${ruttId}/avaktivera`,
    // Barnskjuts. Barnregistret är vårdnadshavarens eget — det finns ingen väg att lista
    // föreningens barn.
    barn: '/api/samakning/v1/barn',
    raderaBarn: (barnId: string) => `/api/samakning/v1/barn/${barnId}`,
    upphämtad: (anmälanId: string) => `/api/samakning/v1/anmalningar/${anmälanId}/upphamtad`,
    framme: (anmälanId: string) => `/api/samakning/v1/anmalningar/${anmälanId}/framme`,
  },
  /**
   * Fastigheter man äger eller är delägare i, med sina ägargrupper och platser.
   *
   * Skickar medvetet ingen organisation i URL:en. En privatperson har ingen — interceptorn ovan
   * utelämnar då X-Organisation-Id, och backend faller tillbaka på ägargruppens person-parter.
   */
  fastighetAgande: {
    minaFastigheter: '/api/fastighet-agande/v1/mina-fastigheter',
    fastigheter: '/api/fastighet-agande/v1/fastigheter',
    fastighet: (id: string) => `/api/fastighet-agande/v1/fastigheter/${id}`,
    ägare: (id: string) => `/api/fastighet-agande/v1/fastigheter/${id}/agare`,
    platser: (id: string) => `/api/fastighet-agande/v1/fastigheter/${id}/platser`,
    plats: (id: string, platsId: string) =>
      `/api/fastighet-agande/v1/fastigheter/${id}/platser/${platsId}`,
  },
  energideklaration: {
    lista: '/api/energideklaration/v1',
    uppdatera: '/api/energideklaration/v1/uppdatera',
    uppslag: '/api/energideklaration/v1/uppslag',
    underlag: (fastighetId: string) => `/api/energideklaration/v1/underlag/${fastighetId}`,
    underlagAuto: (fastighetId: string) => `/api/energideklaration/v1/underlag/${fastighetId}/auto`,
    berakna: '/api/energideklaration/v1/berakna',
    klimatdata: (kommun: string) => `/api/energideklaration/v1/klimatdata/${encodeURIComponent(kommun)}`,
    underlagBerakna: (fastighetId: string) => `/api/energideklaration/v1/underlag/${fastighetId}/berakna`,
  },
  hantverkare: {
    bas: '/api/hantverkare/v1',
    detalj: (id: string) => `/api/hantverkare/v1/${id}`,
    språk: '/api/hantverkare/v1/sprak',
  },
  offerter: {
    bas: '/api/offert/v1/offerter',
    detalj: (id: string) => `/api/offert/v1/offerter/${id}`,
    delar: (id: string) => `/api/offert/v1/offerter/${id}/delar`,
    del: (id: string, delId: string) => `/api/offert/v1/offerter/${id}/delar/${delId}`,
    åtgärder: (id: string, delId: string) =>
      `/api/offert/v1/offerter/${id}/delar/${delId}/atgarder`,
    åtgärd: (id: string, åtgärdId: string) =>
      `/api/offert/v1/offerter/${id}/atgarder/${åtgärdId}`,
    inlägg: (id: string) => `/api/offert/v1/offerter/${id}/inlagg`,
    foton: (id: string, delId: string) =>
      `/api/offert/v1/offerter/${id}/delar/${delId}/foton`,
    foto: (id: string, fotoId: string) => `/api/offert/v1/offerter/${id}/foton/${fotoId}`,
    fotoAi: (id: string, fotoId: string) =>
      `/api/offert/v1/offerter/${id}/foton/${fotoId}/ai`,
    fotoRitning: (id: string, fotoId: string) =>
      `/api/offert/v1/offerter/${id}/foton/${fotoId}/ritning`,
    krav: (id: string, delId: string) => `/api/offert/v1/offerter/${id}/delar/${delId}/krav`,
    kravDetalj: (id: string, delId: string, kravId: string) =>
      `/api/offert/v1/offerter/${id}/delar/${delId}/krav/${kravId}`,
    kravRadera: (id: string, kravId: string) => `/api/offert/v1/offerter/${id}/krav/${kravId}`,
    kravStatus: (id: string, kravId: string) =>
      `/api/offert/v1/offerter/${id}/krav/${kravId}/status`,
    kravKommentarer: (id: string, kravId: string) =>
      `/api/offert/v1/offerter/${id}/krav/${kravId}/kommentarer`,
    kravKommentar: (id: string, kommentarId: string) =>
      `/api/offert/v1/offerter/${id}/kravkommentarer/${kommentarId}`,
    återförsäljare: (id: string, delId: string) =>
      `/api/offert/v1/offerter/${id}/delar/${delId}/aterforsaljare`,
    återförsäljareDetalj: (id: string, delId: string, butikId: string) =>
      `/api/offert/v1/offerter/${id}/delar/${delId}/aterforsaljare/${butikId}`,
    återförsäljareRadera: (id: string, butikId: string) =>
      `/api/offert/v1/offerter/${id}/aterforsaljare/${butikId}`,
    produkter: (id: string, butikId: string) =>
      `/api/offert/v1/offerter/${id}/aterforsaljare/${butikId}/produkter`,
    produkt: (id: string, butikId: string, produktId: string) =>
      `/api/offert/v1/offerter/${id}/aterforsaljare/${butikId}/produkter/${produktId}`,
    produktRadera: (id: string, produktId: string) =>
      `/api/offert/v1/offerter/${id}/produkter/${produktId}`,
    aiGranskning: (id: string, delId: string) =>
      `/api/offert/v1/offerter/${id}/delar/${delId}/ai-granskning`,
    länkar: (id: string, kravId: string) =>
      `/api/offert/v1/offerter/${id}/krav/${kravId}/lankar`,
    länk: (id: string, kravId: string, länkId: string) =>
      `/api/offert/v1/offerter/${id}/krav/${kravId}/lankar/${länkId}`,
    länkRadera: (id: string, länkId: string) => `/api/offert/v1/offerter/${id}/lankar/${länkId}`,
    anteckningar: (id: string) => `/api/offert/v1/offerter/${id}/anteckningar`,
    anteckning: (id: string, aid: string) =>
      `/api/offert/v1/offerter/${id}/anteckningar/${aid}`,
    anteckningsfoton: (id: string, aid: string) =>
      `/api/offert/v1/offerter/${id}/anteckningar/${aid}/foton`,
    anteckningsfoto: (id: string, fid: string) =>
      `/api/offert/v1/offerter/${id}/anteckningsfoton/${fid}`,
    transkribera: (id: string) => `/api/offert/v1/offerter/${id}/anteckningar/transkribera`,
    rot: (id: string) => `/api/offert/v1/offerter/${id}/rot`,
    rotKöpare: (id: string) => `/api/offert/v1/offerter/${id}/rot/kopare`,
    rotUtförare: (id: string) => `/api/offert/v1/offerter/${id}/rot/utforare`,
    svar: (id: string) => `/api/offert/v1/offerter/${id}/svar`,
    bud: (id: string) => `/api/offert/v1/offerter/${id}/bud`,
    tolkaKvitto: (id: string) => `/api/offert/v1/offerter/${id}/material/tolka-kvitto`,
    utförtArbete: (id: string) => `/api/offert/v1/offerter/${id}/utfort-arbete`,
    slutbesiktning: (id: string) => `/api/offert/v1/offerter/${id}/utfort-arbete/slutbesiktning`,
    googleFotonImport: (id: string, delId: string) =>
      `/api/offert/v1/offerter/${id}/delar/${delId}/google-foton`,
    kartmarkörer: (id: string) => `/api/offert/v1/offerter/${id}/kartmarkorer`,
    platshistorik: (platsId: string) => `/api/offert/v1/platser/${platsId}/historik`,
    deltagare: (id: string) => `/api/offert/v1/offerter/${id}/deltagare`,
    deltagareDetalj: (id: string, deltagarId: string) =>
      `/api/offert/v1/offerter/${id}/deltagare/${deltagarId}`,
    förnya: (id: string, deltagarId: string) =>
      `/api/offert/v1/offerter/${id}/deltagare/${deltagarId}/fornya`,
  },
  /**
   * Hantverkarens delade offert. Anropas med en naken axios-klient (se publik/publik-axios.ts),
   * aldrig med axiosInstance — token i sökvägen är hela behörigheten, och en stale org-header från
   * en inloggad flik i samma webbläsare ska inte följa med.
   */
  offertDelning: {
    offert: (token: string) => `/api/offert-delning/v1/${token}`,
    åtgärder: (token: string, delId: string) =>
      `/api/offert-delning/v1/${token}/delar/${delId}/atgarder`,
    åtgärd: (token: string, åtgärdId: string) =>
      `/api/offert-delning/v1/${token}/atgarder/${åtgärdId}`,
    inlägg: (token: string) => `/api/offert-delning/v1/${token}/inlagg`,
    bud: (token: string) => `/api/offert-delning/v1/${token}/bud`,
    plats: (token: string, platsId: string) =>
      `/api/offert-delning/v1/${token}/platser/${platsId}`,
    krav: (token: string, delId: string) =>
      `/api/offert-delning/v1/${token}/delar/${delId}/krav`,
    kravDetalj: (token: string, kravId: string) =>
      `/api/offert-delning/v1/${token}/krav/${kravId}`,
    kravStatus: (token: string, kravId: string) =>
      `/api/offert-delning/v1/${token}/krav/${kravId}/status`,
    länkar: (token: string, kravId: string) =>
      `/api/offert-delning/v1/${token}/krav/${kravId}/lankar`,
    länkRadera: (token: string, länkId: string) =>
      `/api/offert-delning/v1/${token}/lankar/${länkId}`,
    aiGranskning: (token: string, delId: string) =>
      `/api/offert-delning/v1/${token}/delar/${delId}/ai-granskning`,
    återförsäljare: (token: string, delId: string) =>
      `/api/offert-delning/v1/${token}/delar/${delId}/aterforsaljare`,
    återförsäljareRadera: (token: string, butikId: string) =>
      `/api/offert-delning/v1/${token}/aterforsaljare/${butikId}`,
    produkter: (token: string, butikId: string) =>
      `/api/offert-delning/v1/${token}/aterforsaljare/${butikId}/produkter`,
    produktRadera: (token: string, produktId: string) =>
      `/api/offert-delning/v1/${token}/produkter/${produktId}`,
    anteckningar: (token: string) => `/api/offert-delning/v1/${token}/anteckningar`,
    anteckning: (token: string, aid: string) =>
      `/api/offert-delning/v1/${token}/anteckningar/${aid}`,
    anteckningsfoton: (token: string, aid: string) =>
      `/api/offert-delning/v1/${token}/anteckningar/${aid}/foton`,
    anteckningsfoto: (token: string, fid: string) =>
      `/api/offert-delning/v1/${token}/anteckningsfoton/${fid}`,
    transkribera: (token: string) => `/api/offert-delning/v1/${token}/anteckningar/transkribera`,
    rot: (token: string) => `/api/offert-delning/v1/${token}/rot`,
    rotUtförare: (token: string) => `/api/offert-delning/v1/${token}/rot/utforare`,
    svar: (token: string) => `/api/offert-delning/v1/${token}/svar`,
    aiPrisforslag: (token: string) => `/api/offert-delning/v1/${token}/ai/prisforslag`,
    tolkaKvitto: (token: string) => `/api/offert-delning/v1/${token}/material/tolka-kvitto`,
    utförtArbete: (token: string) => `/api/offert-delning/v1/${token}/utfort-arbete`,
    kravKommentarer: (token: string, kravId: string) =>
      `/api/offert-delning/v1/${token}/krav/${kravId}/kommentarer`,
    kravKommentar: (token: string, kommentarId: string) =>
      `/api/offert-delning/v1/${token}/kravkommentarer/${kommentarId}`,
  },
  meddelanden: {
    // Avsändaren är alltid den inloggade och organisationen kommer ur X-Organisation-Id, som
    // request-interceptorn ovan sätter. Kroppen bär bara texten.
    //
    // Nycklarna behåller å/ä/ö, patharna är ren ASCII — samma delning som resten av API:t.
    trådar: '/api/meddelanden/v1/tradar',
    tråd: (id: string) => `/api/meddelanden/v1/tradar/${id}`,
    svara: (id: string) => `/api/meddelanden/v1/tradar/${id}/svar`,
    hemTrådar: (poiId: string) => `/api/meddelanden/v1/hem/${poiId}/tradar`,
    skrivTillHem: (poiId: string) => `/api/meddelanden/v1/hem/${poiId}/tradar`,
  },
  notifieringar: {
    // Mottagaren är alltid den inloggade och organisationen kommer ur X-Organisation-Id,
    // som request-interceptorn ovan sätter. Därför tar ingen av dem några id:n.
    lista: '/api/notifieringar/v1',
    läs: (id: string) => `/api/notifieringar/v1/${id}/las`,
    läsAlla: '/api/notifieringar/v1/las-alla',
    arkivera: (id: string) => `/api/notifieringar/v1/${id}/arkivera`,
    inställningar: '/api/notifieringar/v1/installningar',
  },
  arboristPool: {
    root: '/api/arborist/pool',
    hämta: (id: string) => `/api/arborist/pool/${id}`,
    ratings: (id: string) => `/api/arborist/pool/${id}/ratings`,
    notifiera: '/api/arborist/pool/notifiera',
    dokument: (id: string) => `/api/arborist/pool/${id}/dokument`,
    raderaDokument: (dokumentId: string) => `/api/arborist/pool/dokument/${dokumentId}`,
    schema: {
      tillganglighet: (entryId: string) => `/api/arborist/pool/schema/tillganglighet/${entryId}`,
      allaTillgangliga: '/api/arborist/pool/schema/tillganglighet',
      forfragan: '/api/arborist/pool/schema/forfragan',
      minaForfragningar: '/api/arborist/pool/schema/forfragan/mina',
      forfraganStatus: (id: string) => `/api/arborist/pool/schema/forfragan/${id}/status`,
    },
  },
  utlåningspool: {
    root: "/api/utlaningspool/v1",
    import: "/api/utlaningspool/v1/import",
    interpretImage: "/api/utlaningspool/v1/interpret-image",
    saveBatch: "/api/utlaningspool/v1/batch",
    boka: "/api/utlaningspool/v1/boka",
    minaBokningar: "/api/utlaningspool/v1/mina-bokningar",
    installningar: (orgId: string) => `/api/utlaningspool/v1/${orgId}/installningar`,
    markeModell: (id: string) => `/api/utlaningspool/v1/${id}/marke-modell`,
    manual: (id: string) => `/api/utlaningspool/v1/${id}/manual`,
    hamtning: (id: string) => `/api/utlaningspool/v1/${id}/hamtning`,
    bokning: (id: string) => `/api/utlaningspool/v1/bokningar/${id}`,
  },
  parkering: {
    platser: '/api/parkering/v1/platser',
    plats: (id: string) => `/api/parkering/v1/platser/${id}`,
    ansok: (id: string) => `/api/parkering/v1/platser/${id}/ansok`,
    sagUpp: (id: string) => `/api/parkering/v1/platser/${id}/sag-upp`,
    installningar: '/api/parkering/v1/installningar',
    mitt: '/api/parkering/v1/mitt',
    ansokningar: '/api/parkering/v1/ansokningar',
    godkann: (id: string) => `/api/parkering/v1/ansokningar/${id}/godkann`,
    avsla: (id: string) => `/api/parkering/v1/ansokningar/${id}/avsla`,
    ko: '/api/parkering/v1/ko',
    tackaJa: '/api/parkering/v1/ko/erbjudande/tacka-ja',
    tackaNej: '/api/parkering/v1/ko/erbjudande/tacka-nej',
  },
  roller: {
    root: '/api/roller/v1',
    list: '/api/roller/v1',
    detalj: (rollNamn: string) => `/api/roller/v1/${rollNamn}`,
    menus: (rollNamn: string) => `/api/roller/v1/${rollNamn}/menus`,
  },
  googlePhotos: {
    recent: (orgId: string) => `/api/google-photos/v1/recent/${orgId}`,
    item: (orgId: string, id: string) => `/api/google-photos/v1/item/${orgId}/${id}`,
  },
  superadmin: {
    // Alla RollNamn, inklusive SUPER_ADMIN — inte att förväxla med
    // `organisation.användare.tilldelbaraRoller`, som utelämnar systemrollerna.
    roller: '/api/super-admin/v1/roller',
    assignRole: '/api/super-admin/v1/assign-role',
    revokeRole: '/api/super-admin/v1/revoke-role',
    // Föreningar som läggs upp innan någon i styrelsen registrerat sig. URL:erna är ren ASCII,
    // fältnamnen i kropparna behåller å/ä/ö.
    organisationer: {
      förhandsskapade: '/api/super-admin/v1/organisationer/forhandsskapade',
      förhandsskapa: '/api/super-admin/v1/organisationer/forhandsskapa',
    },
  },
  // Kapitaltillskott, andelstal, överlåtelser och kontrolluppgiften KU55.
  // URL:erna är ren ASCII, fältnamnen i kropparna behåller å/ä/ö.
  kapitaltillskott: {
    hushåll: (orgId: string) => `/api/kapitaltillskott/v1/${orgId}/hushall`,
    andelstal: (orgId: string) => `/api/kapitaltillskott/v1/${orgId}/andelstal`,
    underlag: (orgId: string, år: number) => `/api/kapitaltillskott/v1/${orgId}/underlag/${år}`,
    överlåtelser: (orgId: string) => `/api/kapitaltillskott/v1/${orgId}/overlatelser`,
    kontrolluppgift: (orgId: string, id: string) =>
      `/api/kapitaltillskott/v1/${orgId}/overlatelser/${id}/kontrolluppgift`,
    ku55Fil: (orgId: string, år: number) => `/api/kapitaltillskott/v1/${orgId}/ku55/${år}/fil`,
    säljarkopia: (orgId: string, id: string) =>
      `/api/kapitaltillskott/v1/${orgId}/overlatelser/${id}/kopia`,
  },
  avgifter: {
    // Medlemsavgifterna. Organisationen kommer ur X-Organisation-Id, som request-interceptorn
    // sätter — därför bär ingen av dessa vägar ett orgId.
    //
    // Alla segment är ren ASCII (installningar, oversikt, paminnelser, hushall) även om
    // identifierarna i koden behåller sina svenska tecken.
    inställningar: '/api/avgifter/v1/installningar',
    översikt: '/api/avgifter/v1/oversikt',
    matcha: '/api/avgifter/v1/matcha',
    påminnelser: '/api/avgifter/v1/paminnelser',
    betald: (id: string) => `/api/avgifter/v1/betalningar/${id}/betald`,
    makulera: (id: string) => `/api/avgifter/v1/betalningar/${id}/makulera`,
    banktransaktion: (id: string) => `/api/avgifter/v1/betalningar/${id}/banktransaktion`,
    kandidater: (id: string) => `/api/avgifter/v1/betalningar/${id}/kandidater`,
    hushållsbelopp: (id: string) => `/api/avgifter/v1/hushall/${id}/belopp`,
    hushållsreferens: (id: string) => `/api/avgifter/v1/hushall/${id}/referens`,
    slåIhopHushåll: '/api/avgifter/v1/hushall/slaihop',
    historik: (id: string) => `/api/avgifter/v1/hushall/${id}/historik`,
  },
  imd: {
    // Förbrukning och mätpunkter. Alla segment är ren ASCII (oversikt, matpunkter, avlasning).
    översikt: '/api/imd/v1/oversikt',
    hushållsöversikt: (nyckel: string) => `/api/imd/v1/hushall/${nyckel}/oversikt`,
    mätpunkter: '/api/imd/v1/matpunkter',
    mätpunkt: (id: string) => `/api/imd/v1/matpunkter/${id}`,
    avläsning: (id: string) => `/api/imd/v1/matpunkter/${id}/avlasning`,
    import: '/api/imd/v1/import',
  },
  månadsfakturor: {
    // Månadsavierna. Som avgifterna: organisationen kommer ur X-Organisation-Id, och alla
    // URL-segment är ren ASCII (manadsfakturor, oversikt, perioder, rader, hushall).
    översikt: '/api/manadsfakturor/v1/oversikt',
    perioder: '/api/manadsfakturor/v1/perioder',
    generera: '/api/manadsfakturor/v1/generera',
    skicka: '/api/manadsfakturor/v1/skicka',
    faktura: (id: string) => `/api/manadsfakturor/v1/${id}`,
    rader: (id: string) => `/api/manadsfakturor/v1/${id}/rader`,
    rad: (id: string, radId: string) => `/api/manadsfakturor/v1/${id}/rader/${radId}`,
    makulera: (id: string) => `/api/manadsfakturor/v1/${id}/makulera`,
    historik: (nyckel: string) => `/api/manadsfakturor/v1/hushall/${nyckel}/historik`,
  },
  medlemsregister: {
    bas: '/api/medlemsregister/v1',
    tolka: '/api/medlemsregister/v1/tolka',
    spara: '/api/medlemsregister/v1/spara',
    minaPersonuppgifter: '/api/medlemsregister/v1/mina-personuppgifter',
    minaPersonuppgifterKryptera: '/api/medlemsregister/v1/mina-personuppgifter/kryptera',
    ögonblicksbilder: '/api/medlemsregister/v1/ogonblicksbilder',
    återställÖgonblicksbild: (id: string) =>
      `/api/medlemsregister/v1/ogonblicksbilder/${id}/aterstall`,
    // Hushållets detaljvy. {nyckel} är hushållsnyckeln, inte registrets rad-id.
    hushållsöversikt: (nyckel: string) =>
      `/api/medlemsregister/v1/hushall/${nyckel}/oversikt`,
    hushållsärenden: (nyckel: string) => `/api/medlemsregister/v1/hushall/${nyckel}/arenden`,
    stängHushållsärende: (nyckel: string, ärendeId: string) =>
      `/api/medlemsregister/v1/hushall/${nyckel}/arenden/${ärendeId}/stang`,
  },
  samtycke: {
    bas: '/api/samtycke/v1',
  },
  varme: {
    bas: '/api/varme/v1',
    tolka: '/api/varme/v1/tolka',
    spara: '/api/varme/v1/spara',
    centraler: '/api/varme/v1/centraler',
  },
  el: {
    bas: '/api/el/v1',
    tolka: '/api/el/v1/tolka',
    spara: '/api/el/v1/spara',
  },
  myuplink: {
    system: '/api/myuplink/v1/system',
    mätpunkter: (enhetsId: string) => `/api/myuplink/v1/enhet/${enhetsId}/matpunkter`,
    smartHomeLäge: (systemId: string) => `/api/myuplink/v1/system/${systemId}/smart-home-mode`,
    notifieringar: (systemId: string) => `/api/myuplink/v1/system/${systemId}/notifieringar`,
  },
  backup: {
    download: '/api/backup/v1/download',
    aterstall: '/api/backup/v1/aterstall',
  },
  aktieutdelning: {
    förslag: (orgId: string) => `/api/aktieutdelning/v1/forslag/${orgId}`,
    hämta: (orgId: string) => `/api/aktieutdelning/v1/${orgId}`,
    spara: (orgId: string) => `/api/aktieutdelning/v1/${orgId}`,
    utbetalningar: (orgId: string) => `/api/aktieutdelning/v1/${orgId}/utbetalningar`,
    bokförUtbetalning: (orgId: string) => `/api/aktieutdelning/v1/${orgId}/utbetalningar/bokfor`,
    registreraStämmobeslut: (orgId: string) => `/api/aktieutdelning/v1/${orgId}/registrera-stambeslut`,
  },
  hjärtstartare: {
    närliggande: (lat: number, lng: number, radieMeter = 2000) =>
      `/api/karta/hjartstartare?lat=${lat}&lng=${lng}&radieMeter=${radieMeter}`,
  },
  budget: {
    lista: (orgId: string) => `/api/budget/v1/${orgId}`,
    hämta: (orgId: string, budgetId: string) => `/api/budget/v1/${orgId}/${budgetId}`,
    skapa: (orgId: string) => `/api/budget/v1/${orgId}`,
    uppdatera: (orgId: string, budgetId: string) => `/api/budget/v1/${orgId}/${budgetId}`,
    radera: (orgId: string, budgetId: string) => `/api/budget/v1/${orgId}/${budgetId}`,
    // Antagandena ligger på budgeten och skickas med den — inte som query-parametrar.
    likviditetsprognos: (orgId: string, budgetId: string) =>
        `/api/budget/v1/${orgId}/${budgetId}/likviditetsprognos`,
    // Räknar prognosen på ett osparat underlag, så saldot kan uppdateras medan man skriver.
    förhandsprognos: (orgId: string) => `/api/budget/v1/${orgId}/likviditetsprognos`,
    // Query-nycklarna är ren ASCII även när fältnamnen i svaret bär å/ä/ö.
    historik: (orgId: string, antalÅr = 4) =>
        `/api/budget/v1/${orgId}/historik?antalAr=${antalÅr}`,
    // Föregående års utfall per konto OCH månad, för säsongsberoende kostnader.
    månadsutfall: (orgId: string, budgetId: string) =>
        `/api/budget/v1/${orgId}/${budgetId}/manadsutfall`,
    förslag: (orgId: string, startMånad: string) =>
        `/api/budget/v1/${orgId}/forslag?startManad=${startMånad}`,
    banksaldo: (orgId: string) => `/api/budget/v1/${orgId}/banksaldo`,
  },
  fastighetslan: {
    översikt: (orgId: string) => `/api/fastighetslan/v1/${orgId}`,
    skapa: (orgId: string) => `/api/fastighetslan/v1/${orgId}`,
    uppdatera: (orgId: string, lanId: string) => `/api/fastighetslan/v1/${orgId}/${lanId}`,
    radera: (orgId: string, lanId: string) => `/api/fastighetslan/v1/${orgId}/${lanId}`,
    hämtaFrånBank: (orgId: string) => `/api/fastighetslan/v1/${orgId}/hamta-fran-bank`,
    laddaUppAvi: (orgId: string, lanId: string) => `/api/fastighetslan/v1/${orgId}/${lanId}/ladda-upp-avi`,
  },
  foreningsgrunddata: {
    hämta: (orgId: string) => `/api/foreningsgrunddata/v1/${orgId}`,
    spara: (orgId: string) => `/api/foreningsgrunddata/v1/${orgId}`,
    tolka: (orgId: string) => `/api/foreningsgrunddata/v1/${orgId}/import`,
    senasteTolkning: (orgId: string) => `/api/foreningsgrunddata/v1/${orgId}/import/senaste`,
  },
} as const;
